import { CountryCode, LanguageCode, LANGUAGES } from "@/helpers/languageConstants";
import { useLanguage } from "@/hooks/useLanguage";
import { useWoWToken } from "@/hooks/useWoWToken";
import { useTour } from "@/tours/useTour";
import { HeaderType, NavLink } from "@/types";
import {
  BR as BRFlag,
  DE as DEFlag,
  ES as ESFlag,
  FR as FRFlag,
  IT as ITFlag,
  MX as MXFlag,
  RU as RUFlag,
  US as USFlag,
} from "country-flag-icons/react/3x2";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDown, HelpCircle, Menu } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../zustand/Zustand";
import NextUpdate from "./NextUpdate";
import { WoWTokenDisplay } from "./WoWTokenDisplay";

const FLAG_COMPONENTS = {
  US: USFlag,
  DE: DEFlag,
  FR: FRFlag,
  IT: ITFlag,
  ES: ESFlag,
  MX: MXFlag,
  BR: BRFlag,
  RU: RUFlag,
} as const satisfies Record<CountryCode, unknown>;

const FlagIcon: FC<{ countryCode: CountryCode; label: string }> = ({ countryCode, label }) => {
  const Flag = FLAG_COMPONENTS[countryCode];
  return <Flag title={label} className="h-4 w-6 rounded-sm shadow-sm" />;
};

// ── Style constants ──────────────────────────────────────────────────────────
const NAV_BTN_BASE = 'px-3 py-2 rounded-md text-sm font-medium transition-colors';
const navBtnClass = (active: boolean) =>
  active
    ? `${NAV_BTN_BASE} bg-fp-card text-yellow-500`
    : `${NAV_BTN_BASE} text-gray-300 hover:bg-fp-card hover:text-white`;


// ── Sub-components ───────────────────────────────────────────────────────────
type MenuKey = 'language';

interface LanguageDropdownProps {
  currentLanguage: string;
  isOpen: boolean;
  languageRef: React.RefObject<HTMLDivElement | null>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onToggle: () => void;
  onSelect: (code: LanguageCode) => void;
}

const LanguageDropdown: FC<LanguageDropdownProps> = ({
  currentLanguage, isOpen, languageRef, onMouseEnter, onMouseLeave, onToggle, onSelect,
}) => {
  const selectedLanguage = LANGUAGES.find(lang => lang.code === currentLanguage) || LANGUAGES[0];
  return (
    <div className="relative" ref={languageRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <button
        onClick={onToggle}
        className="px-3 py-2 rounded-md text-sm font-medium bg-fp-card text-white hover:bg-fp-border transition-colors flex items-center space-x-2"
      >
        <FlagIcon countryCode={selectedLanguage.countryCode} label={selectedLanguage.label} />
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-fp-card ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => onSelect(language.code)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-3 transition-colors ${
                  currentLanguage === language.code
                    ? 'bg-fp-border text-yellow-500'
                    : 'text-gray-300 hover:bg-fp-border hover:text-white'
                }`}
                role="menuitem"
              >
                <FlagIcon countryCode={language.countryCode} label={language.label} />
                <span>{language.label}</span>
                {currentLanguage === language.code && <span className="ml-auto text-yellow-500">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TOOL_PATHS = [
  "/dashboard",
  "/tools/flipping",
  "/tools/dropship",
  "/tools/housing",
  "/tools/inventory",
  "/tools/stats",
  "/tools/tsm",
  "/tools/snipe",
  "/guides/tsm-profile",
  "/settings/realm",
  "/settings/personal",
  "/settings/discord",
  "/subscription",
  "/account",
  "/admin/stats",
  "/admin/gold",
  "/admin/user",
  "/admin/item",
  "/admin/snipe-audit",
  "/admin/emails",
];

const LOGO_ONLY_PATHS = [
  "/login", "/register", "/contact-us", "/refund-policy",
  "/privacy-policy", "/terms-and-conditions", "/subscription", "/settings/realm",
  "/affiliate",
];

// ── Main Header ──────────────────────────────────────────────────────────────
const Header: FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
  const menuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  const { userData, logout, isAuthenticated } = useAuth();
  const { currentLanguage, setLanguage } = useLanguage();
  const { startCurrentTour, hasTourForCurrentRoute } = useTour();
  const location = useLocation();

  const handleReload = useCallback(() => { window.location.reload(); }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveMenu(null);
  }, [location]);

  useEffect(() => {
    return () => { if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current); };
  }, []);

  const handleMenuEnter = (menu: MenuKey) => {
    if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
    setActiveMenu(menu);
  };

  const handleMenuLeave = (menu: MenuKey) => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(prev => prev === menu ? null : prev);
    }, 200);
  };

  const toggleMenu = (menu: MenuKey) =>
    setActiveMenu(prev => prev === menu ? null : menu);

  const isActive = (path: string) => location.pathname === path;

  const { tokenData, loading: tokenLoading, error: tokenError } = useWoWToken(userData?.region);

  const scrollToSection = useCallback((sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const homeNavLinks: NavLink[] = useMemo(() => [
    { label: t('nav.ourTools'), href: "#tools", onClick: () => scrollToSection("tools") },
    { label: t('nav.pricing'), href: "#pricing", onClick: () => scrollToSection("pricing") },
    { label: t('nav.faq'), href: "#faq", onClick: () => scrollToSection("faq") },
  ], [scrollToSection, t]);

  const headerType: HeaderType = useMemo(() => {
    const { pathname } = location;
    if (pathname === "/") return "home";
    if (TOOL_PATHS.some(path => pathname === path)) return "insideTool";
    if (LOGO_ONLY_PATHS.includes(pathname)) return "logoOnly";
    return null;
  }, [location]);

  const logoTarget = isAuthenticated ? "/dashboard" : "/";
  const handleLogoClick = useCallback(() => navigate(logoTarget), [navigate, logoTarget]);
  const handleSignIn = useCallback(() => navigate("/login"), [navigate]);
  const handleRegister = useCallback(() => navigate("/register"), [navigate]);
  const handleLanguageChange = useCallback((languageCode: LanguageCode) => {
    setLanguage(languageCode);
    setActiveMenu(null);
  }, [setLanguage]);

  const renderLogo = () => (
    <img
      className="cursor-pointer transition-opacity hover:opacity-80"
      onClick={handleLogoClick}
      src='/logo_text.png'
      alt={t('logoAlt')}
      width={200}
      height="auto"
    />
  );

  // Responsive logo for the in-tool header: icon-only below md, full text logo above.
  const renderResponsiveLogo = () => (
    <>
      <img
        className="md:hidden h-8 w-8 cursor-pointer transition-opacity hover:opacity-80"
        onClick={handleLogoClick}
        src='/logo.png'
        alt={t('logoAlt')}
      />
      <img
        className="hidden md:block w-[200px] cursor-pointer transition-opacity hover:opacity-80"
        onClick={handleLogoClick}
        src='/logo_text.png'
        alt={t('logoAlt')}
      />
    </>
  );

  const renderNavLink = ({ label, href, onClick }: NavLink) => (
    <a
      key={label}
      className="font-semibold text-white transition-colors hover:text-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
      href={href}
      onClick={(e) => { e.preventDefault(); onClick?.(); }}
    >
      {label}
    </a>
  );

  if (headerType === "insideTool") {
    return (
      <header className="bg-fp-header border-b border-fp-border shrink-0">
        <div className="flex justify-between h-14 items-center gap-2 px-3 sm:px-6 lg:px-8">
            <Link to={logoTarget} className="flex items-center shrink-0">
              {renderResponsiveLogo()}
            </Link>

            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              <NextUpdate onImminentReload={handleReload} />
              <WoWTokenDisplay
                tokenData={tokenData}
                loading={tokenLoading}
                error={tokenError}
                showIcon={true}
                className="hidden lg:flex items-center font-semibold text-white space-x-2"
              />
              {hasTourForCurrentRoute && (
                <button
                  type="button"
                  onClick={startCurrentTour}
                  aria-label={t('tour.start')}
                  title={t('tour.start')}
                  className="p-2 rounded-md text-gray-300 bg-fp-card hover:bg-fp-border hover:text-yellow-500 transition-colors"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
              )}
              <LanguageDropdown
                currentLanguage={currentLanguage}
                isOpen={activeMenu === 'language'}
                languageRef={languageRef}
                onMouseEnter={() => handleMenuEnter('language')}
                onMouseLeave={() => handleMenuLeave('language')}
                onToggle={() => toggleMenu('language')}
                onSelect={handleLanguageChange}
              />
              {userData ? (
                <button
                  onClick={logout}
                  className="px-3 md:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  {t('buttons.logout')}
                </button>
              ) : (
                <>
                  <Link to="/login" className={`${navBtnClass(isActive('/login'))} whitespace-nowrap`}>{t('buttons.login')}</Link>
                  <Link
                    to="/register"
                    className="px-3 md:px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                  >
                    {t('buttons.register')}
                  </Link>
                </>
              )}
            </div>
        </div>
      </header>
    );
  }

  if (headerType === "home") return (
    <header className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="relative z-50 flex justify-between" role="navigation" aria-label={t('mainNavAriaLabel')}>
          <div className="flex items-center md:gap-x-12">
            {renderLogo()}
            <div className="hidden md:flex md:gap-x-6">
              {homeNavLinks.map(renderNavLink)}
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            <LanguageDropdown
              currentLanguage={currentLanguage}
              isOpen={activeMenu === 'language'}
              languageRef={languageRef}
              onMouseEnter={() => handleMenuEnter('language')}
              onMouseLeave={() => handleMenuLeave('language')}
              onToggle={() => toggleMenu('language')}
              onSelect={handleLanguageChange}
            />
            <button
              type="button"
              className="hidden md:block font-semibold text-white transition-colors hover:text-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              onClick={handleSignIn}
            >
              {t('buttons.signIn')}
            </button>
            <button
              type="button"
              onClick={handleRegister}
              className="rounded-3xl font-semibold whitespace-nowrap bg-blue-700 text-white px-6 py-2 transition-colors hover:bg-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              {t('buttons.registerNow')}
            </button>
            {/* Mobile menu — replaces the section links hidden below md */}
            <Popover className="relative md:hidden">
              <PopoverButton
                aria-label={t('nav.openMenu')}
                className="p-2 rounded-md text-gray-300 bg-fp-card hover:bg-fp-border hover:text-white transition-colors focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                <Menu className="h-5 w-5" />
              </PopoverButton>
              <PopoverPanel className="absolute right-0 mt-2 w-56 rounded-md bg-fp-card border border-fp-border shadow-lg z-50 py-2">
                {({ close }) => (
                  <div className="flex flex-col">
                    {homeNavLinks.map(({ label, href, onClick }) => (
                      <a
                        key={label}
                        href={href}
                        onClick={(e) => { e.preventDefault(); close(); onClick?.(); }}
                        className="px-4 py-2.5 text-sm font-semibold text-gray-300 hover:bg-fp-border hover:text-white transition-colors"
                      >
                        {label}
                      </a>
                    ))}
                    <div className="border-t border-fp-border my-1" />
                    <button
                      type="button"
                      onClick={() => { close(); handleSignIn(); }}
                      className="px-4 py-2.5 text-left text-sm font-semibold text-gray-300 hover:bg-fp-border hover:text-white transition-colors"
                    >
                      {t('buttons.signIn')}
                    </button>
                  </div>
                )}
              </PopoverPanel>
            </Popover>
          </div>
        </nav>
      </div>
    </header>
  );

  if (headerType === "logoOnly") return (
    <header className="py-10">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="relative z-50 flex justify-between items-center" role="navigation" aria-label={t('simpleNavAriaLabel')}>
          <div className="flex items-center md:gap-x-12">
            {renderLogo()}
          </div>
          <LanguageDropdown
            currentLanguage={currentLanguage}
            isOpen={activeMenu === 'language'}
            languageRef={languageRef}
            onMouseEnter={() => handleMenuEnter('language')}
            onMouseLeave={() => handleMenuLeave('language')}
            onToggle={() => toggleMenu('language')}
            onSelect={handleLanguageChange}
          />
        </nav>
      </div>
    </header>
  );

  return null;
};

export default Header;
